import { useEffect, useMemo, useReducer } from "react";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../reducers";
import {
  fetchClient,
  checkoutCart,
  fetchShippingCost,
  resetShippingCost,
  extendCartSession,
} from "../actions/cartActions";
import { Cart, CheckoutData, FetchClientData } from "../actions/types";
import { PaywayId, ShippingTypeId, WholesaleStatus, InvoiceTypeId } from "../actions/enums";
import ModalAlert from "./ModalAlert";
import {
  getLiquidacionItemsCount,
  validateEmail as utilValidateEmail,
  checkRestrictedCity,
  calculateOrderTotals,
} from "./utilities";
import PersonalInfoStep from "./checkout/PersonalInfoStep";
import ShippingStep from "./checkout/ShippingStep";
import PaymentStep from "./checkout/PaymentStep";
import useTenant from "../tenant/useTenant";

const INITIAL_MODAL_ALERT = {
  show: false,
  message: [] as string[],
  onSuccess: () => {},
  onClose: () => {},
};

interface CheckoutState {
  step: number;
  isLoading: boolean;
  modalAlert: typeof INITIAL_MODAL_ALERT;
  totalCart: number;
  shippingCost: number;
  textButton: string;
  failedSubmit: number;
  cell_phone: string;
  name: string;
  last_name: string;
  document_number: string;
  email: string;
  address: string;
  city: string;
  postal_code: string;
  province: number;
  local_pickup: ShippingTypeId;
  payway_id: PaywayId;
  increase1: number;
  incrementAmount: number;
  discount1: number;
  discountAmount: number;
  isResellerDiscount: boolean;
  restrictedCityError: string;
}

const initialState: CheckoutState = {
  step: 1,
  isLoading: false,
  modalAlert: INITIAL_MODAL_ALERT,
  totalCart: 0,
  shippingCost: 0,
  textButton: "Confirmar compra",
  failedSubmit: 0,
  cell_phone: "",
  name: "",
  last_name: "",
  document_number: "",
  email: "",
  address: "",
  city: "",
  postal_code: "",
  province: 0,
  local_pickup: ShippingTypeId.NONE,
  payway_id: PaywayId.NONE,
  increase1: 0,
  incrementAmount: 0,
  discount1: 0,
  discountAmount: 0,
  isResellerDiscount: false,
  restrictedCityError: "",
};

type CheckoutAction =
  | { type: "SET_FIELD"; field: keyof CheckoutState; value: any }
  | { type: "SET_MULTI"; payload: Partial<CheckoutState> }
  | { type: "SET_STEP"; step: number };

const checkoutReducer = (state: CheckoutState, action: CheckoutAction): CheckoutState => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "SET_MULTI":
      return { ...state, ...action.payload };
    case "SET_STEP":
      return { ...state, step: action.step };
    default:
      return state;
  }
};

const Checkout = () => {
  const tenant = useTenant();
  const { hasResellerLogic } = tenant.features;
  const isLoadingCart = useSelector((state: RootState) => state.cart.isLoading);
  const client = useSelector((state: RootState) => state.cart.client);
  const cart = useSelector((state: RootState) => state.cart.cart);
  const minimumCart = useSelector((state: RootState) => state.cart.minimumCart);
  const minimumPurchaseAmount = useSelector((state: RootState) => state.cart.minimumPurchaseAmount);
  const subTotalCart = useSelector((state: RootState) => +state.cart.totalCart);
  const provinces = useSelector((state: RootState) => state.cart.provinces);
  const shippingCostState = useSelector(
    (state: RootState) => state.cart.shippingCost,
  );
  const incrementSale = useSelector(
    (state: RootState) => state.cart.incrementSale,
  );
  const addressPickup = useSelector(
    (state: RootState) => state.cart.addressPickup,
  );
  const discountCashShop = useSelector(
    (state: RootState) => state.cart.discountCashShop,
  );
  const appliedResellerCoupon = useSelector(
    (state: RootState) => state.cart.appliedResellerCoupon,
  );
  const resellerCoupon = useSelector(
    (state: RootState) => state.cart.resellerCoupon,
  );
  const resellerPercentageDiscount = useSelector(
    (state: RootState) => state.cart.resellerPercentageDiscount,
  );
  const resellerMinAmountDiscount = useSelector(
    (state: RootState) => state.cart.resellerMinAmountDiscount,
  );
  const dispatch: any = useDispatch();

  const liquidacionItemsCount = useMemo(
    () => getLiquidacionItemsCount(cart as Cart[]),
    [cart],
  );

  const hasLiquidacionBlock = useMemo(
    () => liquidacionItemsCount >= minimumCart,
    [liquidacionItemsCount, minimumCart],
  );

  const navigate: any = useNavigate();

  const [state, dispatchState] = useReducer(checkoutReducer, initialState);
  const {
    step,
    isLoading,
    modalAlert,
    totalCart,
    shippingCost,
    textButton,
    failedSubmit,
    cell_phone,
    name,
    last_name,
    document_number,
    email,
    address,
    city,
    postal_code,
    province,
    local_pickup,
    payway_id,
    increase1,
    incrementAmount,
    discount1,
    discountAmount,
    isResellerDiscount,
    restrictedCityError,
  } = state;

  const setModalAlert = (value: typeof INITIAL_MODAL_ALERT) => dispatchState({ type: "SET_FIELD", field: "modalAlert", value });
  const setTotalCart = (value: number) => dispatchState({ type: "SET_FIELD", field: "totalCart", value });
  const setShippingCost = (value: number) => dispatchState({ type: "SET_FIELD", field: "shippingCost", value });
  const setTextButton = (value: string) => dispatchState({ type: "SET_FIELD", field: "textButton", value });
  const setFailedSubmit = (value: number) => dispatchState({ type: "SET_FIELD", field: "failedSubmit", value });
  const setCellPhone = (value: string) => dispatchState({ type: "SET_FIELD", field: "cell_phone", value });
  const setName = (value: string) => dispatchState({ type: "SET_FIELD", field: "name", value });
  const setLastName = (value: string) => dispatchState({ type: "SET_FIELD", field: "last_name", value });
  const setDocumentNumber = (value: string) => dispatchState({ type: "SET_FIELD", field: "document_number", value });
  const setEmail = (value: string) => dispatchState({ type: "SET_FIELD", field: "email", value });
  const setAddress = (value: string) => dispatchState({ type: "SET_FIELD", field: "address", value });
  const setCity = (value: string) => dispatchState({ type: "SET_FIELD", field: "city", value });
  const setPostalCode = (value: string) => dispatchState({ type: "SET_FIELD", field: "postal_code", value });
  const setProvince = (value: number) => dispatchState({ type: "SET_FIELD", field: "province", value });
  const setLocalPickup = (value: ShippingTypeId) => dispatchState({ type: "SET_FIELD", field: "local_pickup", value });
  const setPaywayId = (value: PaywayId) => dispatchState({ type: "SET_FIELD", field: "payway_id", value });
  const setIncrease1 = (value: number) => dispatchState({ type: "SET_FIELD", field: "increase1", value });
  const setIncrementAmount = (value: number) => dispatchState({ type: "SET_FIELD", field: "incrementAmount", value });
  const setDiscount1 = (value: number) => dispatchState({ type: "SET_FIELD", field: "discount1", value });
  const setDiscountAmount = (value: number) => dispatchState({ type: "SET_FIELD", field: "discountAmount", value });
  const setIsResellerDiscount = (value: boolean) => dispatchState({ type: "SET_FIELD", field: "isResellerDiscount", value });
  const setRestrictedCityError = (value: string) => dispatchState({ type: "SET_FIELD", field: "restrictedCityError", value });
  const setIsLoading = (value: boolean) => dispatchState({ type: "SET_FIELD", field: "isLoading", value });

  const onFetchClient = (request: FetchClientData) => {
    dispatch(fetchClient(request));
  };
  const onCheckoutCart = (request: CheckoutData) => {
    dispatch(checkoutCart(request));
  };

  useEffect(() => {
    dispatch(extendCartSession());
  }, [dispatch]);

  useEffect(() => {
    if (!hasResellerLogic) return;

    if (hasLiquidacionBlock) {
      setIsResellerDiscount(false);
      return;
    }

    const shouldApplyResellerDiscount =
      (appliedResellerCoupon === resellerCoupon &&
        appliedResellerCoupon !== null) ||
      (Number(subTotalCart) > Number(resellerMinAmountDiscount) &&
        Number(resellerMinAmountDiscount) > 0);

    setIsResellerDiscount(shouldApplyResellerDiscount);
  }, [
    hasResellerLogic,
    appliedResellerCoupon,
    resellerCoupon,
    subTotalCart,
    resellerMinAmountDiscount,
    hasLiquidacionBlock,
  ]);

  const verifySession = () => {
    if (cart?.length === 0 && !isLoadingCart) {
      setModalAlert({
        ...modalAlert,
        show: true,
        message: [
          "Lo sentimos, no hemos podido completar la carga del carrito de compras.",
          "Esto puede deberse a un problema de conexión.",
          "Te pedimos que vuelvas a cargar los productos para poder finalizar la compra.",
        ],
        onSuccess: () => {
          setModalAlert(INITIAL_MODAL_ALERT);
          navigate("/");
        },
        onClose: () => {
          setModalAlert(INITIAL_MODAL_ALERT);
          navigate("/");
        },
      });
    }
  };

  useEffect(() => {
    if (client) {
      dispatchState({
        type: "SET_MULTI",
        payload: {
          name: client.name,
          last_name: client.last_name,
          document_number: client.document_number,
          email: client.email,
          address: client.address,
          city: client.city,
          postal_code: client.postal_code,
          province: client.province,
        },
      });
    }
  }, [client]);

  const handleFetchClient = () => {
    if (cell_phone.length !== 10) {
      setModalAlert({
        ...modalAlert,
        show: true,
        message: ["WhatsApp incorrecto"],
        onSuccess: () => setModalAlert(INITIAL_MODAL_ALERT),
        onClose: () => setModalAlert(INITIAL_MODAL_ALERT),
      });
    } else {
      onFetchClient({ cell_phone, client_id: null });
      setTotalCart(subTotalCart);
    }
  };

  const handlePersonalInfo = () => {
    if (
      name?.length > 0 &&
      last_name?.length > 0 &&
      cell_phone?.length === 10 &&
      validateEmail() &&
      document_number?.length > 6
    ) {
      dispatchState({ type: "SET_STEP", step: 2 });
      window.scrollTo(0, 0);
      verifySession();
    } else {
      setModalAlert({
        ...modalAlert,
        show: true,
        message: ["Complete correctamente los campos."],
        onSuccess: () => setModalAlert(INITIAL_MODAL_ALERT),
        onClose: () => setModalAlert(INITIAL_MODAL_ALERT),
      });
    }
  };

  function validateEmail() {
    if (utilValidateEmail(email)) {
      return true;
    }
    setModalAlert({
      ...modalAlert,
      show: true,
      message: ["Ingresar un mail válido."],
      onSuccess: () => setModalAlert(INITIAL_MODAL_ALERT),
      onClose: () => setModalAlert(INITIAL_MODAL_ALERT),
    });
    return false;
  }

  const handleSendInfo = () => {
    if (
      local_pickup !== ShippingTypeId.NONE &&
      address?.length > 0 &&
      city?.length > 0 &&
      postal_code?.length > 0 &&
      province !== 0
    ) {
      if (local_pickup === ShippingTypeId.COURIER_SHIPPING) {
        dispatch(fetchShippingCost(province));
      }
      dispatchState({ type: "SET_STEP", step: 3 });
      window.scrollTo(0, 0);
    } else {
      setModalAlert({
        ...modalAlert,
        show: true,
        message: ["Todos los campos son requeridos."],
        onSuccess: () => setModalAlert(INITIAL_MODAL_ALERT),
        onClose: () => setModalAlert(INITIAL_MODAL_ALERT),
      });
    }
  };

  const handleChangePayWay = (payWayValue: PaywayId) => {
    setPaywayId(payWayValue);
    if (payWayValue === PaywayId.CARD) {
      setIncrease1(incrementSale);
      setDiscount1(0);
      setTextButton("Pagar");
    } else {
      setIncrease1(0);
      if (hasResellerLogic && isResellerDiscount) {
        setDiscount1(Number(resellerPercentageDiscount));
      } else {
        setDiscount1(discountCashShop);
      }
      setTextButton("Confirmar compra");
    }
  };

  const handleBack = () => {
    resetTotalCart();
    if (step === 3) {
      dispatchState({ type: "SET_STEP", step: 2 });
      window.scrollTo(0, 0);
    } else {
      dispatchState({ type: "SET_STEP", step: 1 });
      window.scrollTo(0, 0);
    }
  };

  const resetTotalCart = () => {
    dispatch(resetShippingCost());
    dispatchState({
      type: "SET_MULTI",
      payload: {
        totalCart: subTotalCart,
        payway_id: PaywayId.NONE,
        shippingCost: 0,
        incrementAmount: 0,
        increase1: 0,
        discount1: 0,
      },
    });
  };

  const onSetTotalCart = () => {
    const results = calculateOrderTotals(subTotalCart, shippingCost, increase1, discount1);
    dispatchState({
      type: "SET_MULTI",
      payload: {
        incrementAmount: results.incrementAmount,
        discountAmount: results.discountAmount,
        totalCart: results.totalCart,
      },
    });
  };

  useEffect(() => {
    if (local_pickup === ShippingTypeId.COURIER_SHIPPING) {
      setShippingCost(shippingCostState);
    } else {
      setShippingCost(0);
    }
  }, [shippingCostState, local_pickup]);

  useEffect(() => {
    onSetTotalCart();
    // eslint-disable-next-line
  }, [shippingCost, payway_id]);

  useEffect(() => {
    setRestrictedCityError(
      checkRestrictedCity(
        city,
        postal_code,
        cell_phone,
        tenant.restrictedZipCodes,
        tenant.allowedCellPhones,
      ),
    );
  }, [city, postal_code, cell_phone, tenant]);

  const handleSubmit = () => {
    if (payway_id === PaywayId.NONE) {
      setModalAlert({
        ...modalAlert,
        show: true,
        message: ["Todos los campos son requeridos."],
        onSuccess: () => setModalAlert(INITIAL_MODAL_ALERT),
        onClose: () => setModalAlert(INITIAL_MODAL_ALERT),
      });
    } else {
      if (totalCart > 0 && subTotalCart > 0) {
        setIsLoading(true);
        const client_id = client ? client.id : "";
        const wholesale = hasResellerLogic && isResellerDiscount
          ? WholesaleStatus.RESELLER
          : WholesaleStatus.WHOLESALE;
        const invoice_type_id = InvoiceTypeId.CONSUMIDOR_FINAL;
        onCheckoutCart({
          shop_id: tenant.shopId,
          post: tenant.post,
          wholesale,
          invoice_type_id,
          client_id,
          cell_phone,
          name,
          last_name,
          document_number,
          email,
          address,
          city,
          postal_code,
          province,
          local_pickup,
          payway_id,
          increase1,
          discount1,
          totalCart,
          subTotalCart,
          shippingCost,
          isResellerDiscount,
        });
      } else {
        if (failedSubmit > 1) {
          setModalAlert({
            ...modalAlert,
            show: true,
            message: ["Ha ocurrido un error, vuelva a cargar sus datos."],
            onSuccess: () => setModalAlert(INITIAL_MODAL_ALERT),
            onClose: () => setModalAlert(INITIAL_MODAL_ALERT),
          });
        }
        setFailedSubmit(failedSubmit + 1);
      }
    }
  };

  return (
    <Container>
      {step === 1 && (
        <PersonalInfoStep
          cellPhone={cell_phone}
          name={name}
          lastName={last_name}
          email={email}
          documentNumber={document_number}
          setCellPhone={setCellPhone}
          setName={setName}
          setLastName={setLastName}
          setEmail={setEmail}
          setDocumentNumber={setDocumentNumber}
          handleFetchClient={handleFetchClient}
          handleNext={handlePersonalInfo}
        />
      )}
      {step === 2 && (
        <ShippingStep
          localPickup={local_pickup}
          shippingOptions={tenant.shippingOptions}
          address={address}
          city={city}
          postalCode={postal_code}
          province={province}
          provinces={provinces}
          restrictedCityError={restrictedCityError}
          setLocalPickup={setLocalPickup}
          setAddress={setAddress}
          setCity={setCity}
          setPostalCode={setPostalCode}
          setProvince={setProvince}
          handleBack={handleBack}
          handleNext={handleSendInfo}
        />
      )}
      {step === 3 && (
        <PaymentStep
          hasLiquidacionBlock={hasLiquidacionBlock}
          subTotalCart={subTotalCart}
          resellerMinAmountDiscount={resellerMinAmountDiscount}
          liquidacionItemsCount={liquidacionItemsCount}
          minimumCart={minimumCart}
          minimumPurchaseAmount={minimumPurchaseAmount}
          paywayId={payway_id}
          localPickup={local_pickup}
          addressPickup={addressPickup}
          increase1={increase1}
          discount1={discount1}
          isResellerDiscount={isResellerDiscount}
          shippingCost={shippingCost}
          incrementAmount={incrementAmount}
          discountAmount={discountAmount}
          totalCart={totalCart}
          isLoading={isLoading}
          textButton={textButton}
          handleChangePayWay={handleChangePayWay}
          handleBack={handleBack}
          handleSubmit={handleSubmit}
        />
      )}
      <ModalAlert
        show={modalAlert.show}
        onClose={modalAlert.onClose}
        message={modalAlert.message}
        onSuccess={modalAlert.onSuccess}
      />
    </Container>
  );
};

export default Checkout;
